import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import { toast } from "react-toastify";
import { getSalatCategories, getSalatProducts, getStates } from "../../api";
import { api_url } from "../../constant";
import { useCart } from "../../store/cartProvider";
import ConvertToArabicNumbers from "../../utils/convert_numbers";
import Skelton from "../../icons/skelton";
import notFound from "../../images/not-found.png";

const CartPlusIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.5 8V5m-1.5 1.5h3" />
  </svg>
);

const ProductCard = ({ product }) => {
  const { t, i18n } = useTranslation();
  const cart = useCart();
  const isRtl = i18n.language === "ar";
  const image = product.images?.[0];
  const price = parseFloat(product.price);
  const formattedPrice = isRtl ? ConvertToArabicNumbers(price.toFixed(2)) : price.toFixed(2);
  const name = i18n.language === "en" && product.name_en ? product.name_en : product.name;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const maxQuantity = product.quantity != null ? product.quantity : null;
    cart.addItem(product.id, 1, maxQuantity);
    toast.success(t("salatMahsool.product.addedToCart"));
  };

  return (
    <Link
      to={`/home/salatMahsool/${product.id}`}
      className="group relative flex flex-col w-full bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-primary transition-all duration-200 h-[19rem] lg:h-80 min-w-full"
    >
      <div className="relative w-full h-28 lg:h-36 shrink-0 overflow-hidden bg-gray-50">
        <img
          src={image ? `${api_url}/${image}` : notFound}
          className={`w-full h-full transition-transform duration-200 group-hover:scale-110 ${
            image ? "object-cover" : "object-contain p-6"
          }`}
          alt={name}
        />
        {product._category && (
          <span className="absolute top-2 rtl:right-2 ltr:left-2 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
            {i18n.language === "en" && product._category.name_en
              ? product._category.name_en
              : product._category.name}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 px-3 py-2">
        <div className="font-bold text-base lg:text-lg text-gray-800 line-clamp-2">{name}</div>
        {product._state && (
          <div className="text-xs text-gray-400 mt-0.5">
            {i18n.language === "en" && product._state.name_en
              ? product._state.name_en
              : product._state.name}
          </div>
        )}
        <div className="flex items-end justify-between mt-auto pt-2">
          <div className="text-md font-bold text-primary leading-tight">
            {formattedPrice} {t("common.currency")}
            {product._unit && (
              <span className="block text-xs text-gray-500 font-normal">
                / {product._unit.name}
              </span>
            )}
          </div>
          <button
            onClick={handleQuickAdd}
            title={t("salatMahsool.product.addToCart")}
            className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full bg-primary text-white hover:bg-secondary transition-colors"
          >
            <CartPlusIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Link>
  );
};

function SalatCatalog() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState("");
  const [stateId, setStateId] = useState("");
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname,
    title: "SalatMahsool Catalog",
  });

  const { data: categories } = useQuery({
    queryKey: ["salatCategories"],
    queryFn: () => getSalatCategories(),
    initialData: [],
  });

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: () => getStates(),
    initialData: [],
  });

  const {
    data: products,
    isFetching,
  } = useQuery({
    queryKey: ["salatProducts", page, categoryId, stateId, debouncedSearchTerm],
    queryFn: () =>
      getSalatProducts(
        page,
        20,
        categoryId || undefined,
        stateId || undefined,
        debouncedSearchTerm || undefined
      ),
    onSuccess: (res) => {
      setData((prev) => (page === 1 ? res.data : [...prev, ...res.data]));
    },
    refetchOnWindowFocus: false,
    initialData: { data: [], total: 0, pageCount: 1, page: 1 },
  });

  useEffect(() => {
    setPage(1);
  }, [categoryId, stateId, debouncedSearchTerm]);

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="w-full lg:px-20 px-4 py-8">
      <Helmet>
        <title>{t("salatMahsool.catalog.title")} - Mahsool</title>
      </Helmet>

      <h1 className="font-bold text-2xl lg:text-3xl mb-6 text-primary">
        {t("salatMahsool.catalog.title")}
      </h1>

      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("salatMahsool.catalog.searchPlaceholder")}
          className="w-full lg:w-80 border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full lg:w-60 border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">{t("salatMahsool.catalog.allCategories")}</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {i18n.language === "en" && category.name_en ? category.name_en : category.name}
            </option>
          ))}
        </select>
        <select
          value={stateId}
          onChange={(e) => setStateId(e.target.value)}
          className="w-full lg:w-60 border border-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">{t("salatMahsool.catalog.allStates")}</option>
          {states.map((state) => (
            <option key={state.id} value={state.id}>
              {i18n.language === "en" && state.name_en ? state.name_en : state.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid w-full grid-cols-2 gap-5 lg:gap-4 lg:grid-cols-5">
        {data.length === 0 && !isFetching ? (
          <div className="flex flex-col col-span-2 mt-10 justify-center text-center lg:col-span-5">
            <img src={notFound} className="mx-auto" alt="" />
            <div className="h-6"></div>
            <div className="flex items-center mx-auto justify-center h-12 text-lg font-bold">
              {t("salatMahsool.catalog.empty")}
            </div>
          </div>
        ) : (
          data.map((product) => <ProductCard product={product} key={product.id} />)
        )}
        {isFetching &&
          [...Array(5).keys()].map((i) => <Skelton key={`skelton-${i}`} />)}
      </div>

      {!isFetching && products.pageCount > products.page && (
        <button
          onClick={() => setPage((p) => p + 1)}
          className="bg-primary mx-auto mt-8 block text-white rounded-full py-2 px-6 hover:scale-105 transition disabled:opacity-50"
        >
          {t("common.load_more")}
        </button>
      )}
    </div>
  );
}

export default SalatCatalog;
